import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import SearchIcon from '@mui/icons-material/Search'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'

import { MUTED_TEXT_DARK } from '../../styles/colors'
import { ControlledInput } from '../../styles/TextField'
import { ToolGroup, ToolButton, SquareIconButton } from './editorChrome'

const SearchInput = styled(ControlledInput)`
  width: 180px;
  height: 30px;
  padding: 0 10px;
`

const ResultLabel = styled.span`
  min-width: 48px;
  color: ${MUTED_TEXT_DARK};
  font-size: 13px;
  text-align: center;
  white-space: nowrap;
`

function DocumentSearchToolbar({
  open,
  query,
  searching,
  searched,
  resultCount,
  activeIndex,
  onOpen,
  onClose,
  onQueryChange,
  onSearch,
  onPrevious,
  onNext
}) {
  const searchRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined

    function handleOutsideClick(event) {
      if (!searchRef.current?.contains(event.target)) onClose()
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [open, onClose])

  if (!open) {
    return (
      <ToolButton onClick={onOpen} aria-label="Search document">
        <SearchIcon /> Search
      </ToolButton>
    )
  }

  return (
    <ToolGroup
      ref={searchRef}
      as="form"
      role="search"
      onSubmit={event => {
        event.preventDefault()
        onSearch(event)
      }}>
      <SearchInput
        autoFocus
        aria-label="Search document text"
        placeholder="Search this document"
        value={query}
        onChange={event => onQueryChange(event.target.value)}
        onKeyDown={event => {
          if (event.key !== 'Enter' || !searched || !resultCount || searching) return
          event.preventDefault()
          onNext()
        }}
        readOnly={searching}
        aria-busy={searching}
      />
      <SquareIconButton type="submit" aria-label="Run document search" disabled={!query.trim() || searching}>
        <SearchIcon />
      </SquareIconButton>
      {searched && (
        <>
          <ResultLabel role="status">{resultCount ? `${activeIndex + 1} of ${resultCount}` : 'No results'}</ResultLabel>
          <SquareIconButton type="button" aria-label="Previous search result" onClick={onPrevious} disabled={!resultCount}>
            <ChevronLeftIcon />
          </SquareIconButton>
          <SquareIconButton type="button" aria-label="Next search result" onClick={onNext} disabled={!resultCount}>
            <ChevronRightIcon />
          </SquareIconButton>
          <SquareIconButton type="button" aria-label="Close document search" onClick={onClose}>
            <CloseIcon />
          </SquareIconButton>
        </>
      )}
    </ToolGroup>
  )
}

DocumentSearchToolbar.propTypes = {
  open: PropTypes.bool.isRequired,
  query: PropTypes.string.isRequired,
  searching: PropTypes.bool.isRequired,
  searched: PropTypes.bool.isRequired,
  resultCount: PropTypes.number.isRequired,
  activeIndex: PropTypes.number.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onQueryChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired
}

export default DocumentSearchToolbar
